import { ALL_APP_IDS, ALL_VIEW_IDS } from 'constants/appConstants';
import _ from 'lodash';
import { useGenerateShipmentPdfMutation } from 'store/slices/apiV1/inventory';
import { useGeneratePurchaseOrderPdfMutation } from 'store/slices/apiV1/procurement';
import { useGetClientV2BomsQuery } from 'store/slices/clientV2/boms';
import {
  useDeleteKitRequestLinesMutation,
  useGetKitRequestsQuery,
  useUpdateKitRequestLinesMutation,
} from 'store/slices/clientV2/kitRequests';
import { useUpdateOrgPartMutation } from 'store/slices/clientV2/parts';
import { useGetProgramsQuery } from 'store/slices/clientV2/programs';
import { useGetPurchaseOrdersClientV2Query } from 'store/slices/clientV2/purchaseOrders';
import {
  useUpdatePurchaseRequestGroupsMutation,
  useApprovePurchaseRequestGroupsActionMutation,
  useUpdatePurchaseRequestsV2Mutation,
} from 'store/slices/clientV2/purchaseRequests';
import { useGetShipmentsClientV2Query } from 'store/slices/clientV2/shipments';
import {
  useApproveRfqsMutation,
  useArchiveRfqsMutation,
  useDeleteRfqLinesMutation,
  useDeleteRfqsMutation,
  useGenerateRfqPdfMutation,
  useGetRfqsQuery,
  useUnapproveRfqsMutation,
  useUnarchiveRfqsMutation,
  useUnvoidRfqsMutation,
  useUpdateRfqLinesMutation,
  useUpdateRfqsMutation,
  useVoidRfqsMutation,
} from 'store/slices/clientV2/sourcing';
import {
  useApproveSupplierBillActionMutation,
  useArchiveSupplierBillActionMutation,
  useDeleteSupplierBillLineMutation,
  useDeleteSupplierBillMutation,
  useGetSupplierBillsQuery,
  useUnapproveSupplierBillActionMutation,
  useUnarchiveSupplierBillActionMutation,
  useUnvoidSupplierBillActionMutation,
  useUpdateSupplierBillLineMutation,
  useUpdateSupplierBillMutation,
  useVoidSupplierBillActionMutation,
} from 'store/slices/clientV2/supplierBills';
import {
  useApproveSupplierQuoteLineActionMutation,
  useArchiveSupplierQuoteActionMutation,
  useDeleteSupplierQuoteLineMutation,
  useDeleteSupplierQuoteMutation,
  useGetSupplierQuotesQuery,
  useUnapproveSupplierQuoteLineActionMutation,
  useUnarchiveSupplierQuoteActionMutation,
  useUnvoidSupplierQuoteLineActionMutation,
  useUpdateSupplierQuoteLineMutation,
  useUpdateSupplierQuoteMutation,
  useVoidSupplierQuoteLineActionMutation,
} from 'store/slices/clientV2/supplierQuotes';
import {
  useApproveUnifiedBomsMutation,
  useArchiveUnifiedBomsMutation,
  useDeleteUnifiedBomsMutation,
  useGetUnifiedBomsQuery,
  useLockUnifiedBomsMutation,
  useUnapproveUnifiedBomsMutation,
  useUnarchiveUnifiedBomsMutation,
  useUnlockUnifiedBomsMutation,
  useUpdateUnifiedBomsMutation,
} from 'store/slices/unified/bom';
import { PatchMutation } from 'types/api';
import { RecordType } from 'types/apps';
import { BomLineSchema, BomSchema } from 'types/clientV2/boms';
import { OrgPartSchema, PublicPartSchema } from 'types/clientV2/parts';
import { ProgramSchema } from 'types/clientV2/programs';
import {
  PurchaseRequestGroupSchema,
  PurchaseRequestSchema,
} from 'types/clientV2/purchaseRequests';
import { ShipmentLineSchema, ShipmentSchema } from 'types/clientV2/shipments';
import { BarcodeLabelSchema } from 'types/clientV2/stock';
import { CustomPropertyModel } from 'types/customProperty';
import {
  RelatedRecordType,
  StockDocType,
  StockLotSchemaClientV2,
} from 'types/inventory';
import { KitRequestLineSchema, KitRequestSchema } from 'types/kitting';
import { Org } from 'types/org';
import {
  NoPartPurchaseLineSchema,
  PurchaseEvent,
  PurchaseOrderLineSchema,
  PurchaseOrderSchemaClientV2,
  SupplierBillLineSchema,
  SupplierBillSchema,
} from 'types/procurement';
import { ProductionRunPart } from 'types/production';
import {
  RfqLineSchema,
  RfqSchema,
  RfqSupplierJoinSchema,
  SupplierQuoteLineSchema,
  SupplierQuoteSchema,
} from 'types/sourcing';
import { OrgSupplier } from 'types/suppliers';
import { BomUnifiedSchema } from 'types/unified/boms';
import { UnitOfMeasureDetails } from 'types/unitsOfMeasure';
import { formatToShortDate } from 'utils/dates';
import { formatPrice } from 'utils/functions';
import {
  getAddressLabel,
  getOrgPartOrDescription,
  getPart,
  getPartOrDescription,
  getV2OrgPartFromV2OrgParts,
  getV2OrgPartOrDescription,
} from 'utils/nameGetters';
import {
  bomStatusSchema,
  getBomStatusSteps,
  getKitRequestStatusSteps,
  getProductionRunStatusSteps,
  getPurchaseOrderStatusSteps,
  getRfqStatusSteps,
  getShipmentStatusSteps,
  getStockLotStatusSteps,
  getSupplierBillStatusSteps,
  getSupplierPurchaseOrderStatusSteps,
  getSupplierQuoteStatusSteps,
  getUnifiedBomStatusSteps,
  kitRequestStatusSchema,
  productionRunStatusSchema,
  purchaseOrderStatusSchema,
  rfqStatusSchema,
  shipmentStatusSchema,
  supplierBillStatusSchema,
  supplierPurchaseOrderStatusSchema,
  supplierQuoteStatusSchema,
} from 'utils/statusGetters';

export enum FlagshipModelOptions {
  ORG_SUPPLIER = 'orgsupplier',
  PURCHASE_REQUEST = 'purchaserequest',
  PURCHASE_REQUEST_GROUP = 'purchaserequestgroup',
  RFQ = 'rfq',
  RFQ_LINE = 'rfqline',
  RFQ_SUPPLIER_JOIN = 'rfqsupplierjoin',
  SUPPLIER_QUOTE = 'supplierquote',
  SUPPLIER_QUOTE_LINE = 'supplierquoteline',
  SUPPLIER_BILL = 'supplierbill',
  SUPPLIER_BILL_LINE = 'supplierbillline',
  STOCK_LOT = 'stocklot',
  SHIPMENT = 'shipment',
  SHIPMENT_LINE = 'shipmentline',
  KIT_REQUEST = 'kitrequest',
  KIT_REQUEST_LINE = 'kitrequestline',
  ORG_PART = 'orgpart',
  V2_ORG_PART = 'v2orgpart',
  PURCHASE = 'purchase',
  PURCHASE_LINE = 'purchaseline',
  NO_PART_PURCHASE_LINE = 'nopartpurchaseline',
  PURCHASE_EVENT = 'purchaseevent',
  PRODUCTION_RUN = 'productionrun',
  BOM = 'bom',
  BOM_LINE = 'bomline',
  BARCODE_LABEL = 'barcodelabel',
  ORG = 'org',
}

export enum ALL_RECORD_TYPE_IDS {
  RFQ = 'RFQ',
  RFQ_LINE = 'RFQ_LINE',
  RFQ_SUPPLIER_JOIN = 'RFQ_SUPPLIER_JOIN',
  QUOTE = 'QUOTE',
  QUOTE_LINE = 'QUOTE_LINE',
  SUPPLIER_BILL = 'SUPPLIER_BILL',
  SUPPLIER_BILL_LINE = 'SUPPLIER_BILL_LINE',
  STOCK_LOT = 'STOCK_LOT',
  SHIPMENT = 'SHIPMENT',
  SHIPMENT_LINE = 'SHIPMENT_LINE',
  KIT_REQUEST = 'KIT_REQUEST',
  KIT_REQUEST_LINE = 'KIT_REQUEST_LINE',
  ORG_SUPPLIER = 'ORG_SUPPLIER',
  V2_ORG_PART = 'V2_ORG_PART',
  PURCHASE = 'PURCHASE',
  PURCHASE_LINE = 'PURCHASE_LINE',
  NO_PART_PURCHASE_LINE = 'NO_PART_PURCHASE_LINE',
  PURCHASE_REQUEST = 'PURCHASE_REQUEST',
  PURCHASE_REQUEST_GROUP = 'PURCHASE_REQUEST_GROUP',
  PURCHASE_EVENT = 'PURCHASE_EVENT',
  PRODUCTION_RUN = 'PRODUCTION_RUN',
  PRODUCTION_RUN_PART = 'PRODUCTION_RUN_PART',
  BOM = 'BOM',
  BOM_LINE = 'BOM_LINE',
  PART = 'PART',
  BARCODE_LABEL = 'BARCODE_LABEL',
  UNIFIED_BOM = 'UNIFIED_BOM',
  ORG = 'ORG',
  CART_PURCHASE_REQUEST_GROUP = 'CART_PURCHASE_REQUEST_GROUP',
  RFQ_PURCHASE_REQUEST_GROUP = 'RFQ_PURCHASE_REQUEST_GROUP',
  CART_PURCHASE_REQUEST = 'CART_PURCHASE_REQUEST',
  RFQ_PURCHASE_REQUEST = 'RFQ_PURCHASE_REQUEST',
  SUPPLIER_PURCHASE_ORDER = 'SUPPLIER_PURCHASE_ORDER',
  SUPPLIER_PURCHASE_ORDER_LINE = 'SUPPLIER_PURCHASE_ORDER_LINE',
}

export type RecordTypeMap = {
  [ALL_RECORD_TYPE_IDS.RFQ]: RecordType<RfqSchema>;
  [ALL_RECORD_TYPE_IDS.RFQ_LINE]: RecordType<RfqLineSchema>;
  [ALL_RECORD_TYPE_IDS.RFQ_SUPPLIER_JOIN]: RecordType<RfqSupplierJoinSchema>;
  [ALL_RECORD_TYPE_IDS.QUOTE]: RecordType<SupplierQuoteSchema>;
  [ALL_RECORD_TYPE_IDS.QUOTE_LINE]: RecordType<SupplierQuoteLineSchema>;
  [ALL_RECORD_TYPE_IDS.SUPPLIER_BILL]: RecordType<SupplierBillSchema>;
  [ALL_RECORD_TYPE_IDS.SUPPLIER_BILL_LINE]: RecordType<SupplierBillLineSchema>;
  [ALL_RECORD_TYPE_IDS.STOCK_LOT]: RecordType<StockLotSchemaClientV2>;
  [ALL_RECORD_TYPE_IDS.SHIPMENT]: RecordType<ShipmentSchema>;
  [ALL_RECORD_TYPE_IDS.SHIPMENT_LINE]: RecordType<ShipmentLineSchema>;
  [ALL_RECORD_TYPE_IDS.KIT_REQUEST]: RecordType<KitRequestSchema>;
  [ALL_RECORD_TYPE_IDS.KIT_REQUEST_LINE]: RecordType<KitRequestLineSchema>;
  [ALL_RECORD_TYPE_IDS.ORG_SUPPLIER]: RecordType<OrgSupplier>;
  [ALL_RECORD_TYPE_IDS.V2_ORG_PART]: RecordType<OrgPartSchema>;
  [ALL_RECORD_TYPE_IDS.PURCHASE]: RecordType<PurchaseOrderSchemaClientV2>;
  [ALL_RECORD_TYPE_IDS.PURCHASE_LINE]: RecordType<PurchaseOrderLineSchema>;
  [ALL_RECORD_TYPE_IDS.NO_PART_PURCHASE_LINE]: RecordType<NoPartPurchaseLineSchema>;
  [ALL_RECORD_TYPE_IDS.PURCHASE_REQUEST]: RecordType<PurchaseRequestSchema>;
  [ALL_RECORD_TYPE_IDS.PURCHASE_REQUEST_GROUP]: RecordType<PurchaseRequestGroupSchema>;
  [ALL_RECORD_TYPE_IDS.PURCHASE_EVENT]: RecordType<PurchaseEvent>;
  [ALL_RECORD_TYPE_IDS.PRODUCTION_RUN]: RecordType<ProgramSchema>;
  [ALL_RECORD_TYPE_IDS.PRODUCTION_RUN_PART]: RecordType<ProductionRunPart>;
  [ALL_RECORD_TYPE_IDS.BOM]: RecordType<BomSchema>;
  [ALL_RECORD_TYPE_IDS.BOM_LINE]: RecordType<BomLineSchema>;
  [ALL_RECORD_TYPE_IDS.PART]:
    | RecordType<OrgPartSchema>
    | RecordType<PublicPartSchema>;
  [ALL_RECORD_TYPE_IDS.BARCODE_LABEL]: RecordType<BarcodeLabelSchema>;
  [ALL_RECORD_TYPE_IDS.UNIFIED_BOM]: RecordType<BomUnifiedSchema>;
  [ALL_RECORD_TYPE_IDS.ORG]: RecordType<Org>;
  [ALL_RECORD_TYPE_IDS.CART_PURCHASE_REQUEST_GROUP]: RecordType<PurchaseRequestGroupSchema>;
  [ALL_RECORD_TYPE_IDS.RFQ_PURCHASE_REQUEST_GROUP]: RecordType<PurchaseRequestGroupSchema>;
  [ALL_RECORD_TYPE_IDS.CART_PURCHASE_REQUEST]: RecordType<PurchaseRequestSchema>;
  [ALL_RECORD_TYPE_IDS.RFQ_PURCHASE_REQUEST]: RecordType<PurchaseRequestSchema>;
  [ALL_RECORD_TYPE_IDS.SUPPLIER_PURCHASE_ORDER]: RecordType<PurchaseOrderSchemaClientV2>;
  [ALL_RECORD_TYPE_IDS.SUPPLIER_PURCHASE_ORDER_LINE]: RecordType<PurchaseOrderLineSchema>;
};

export const ALL_RECORD_TYPES: RecordTypeMap = {
  [ALL_RECORD_TYPE_IDS.ORG]: {
    id: ALL_RECORD_TYPE_IDS.ORG,
    singularName: 'Org',
    pluralName: 'Orgs',
    app: ALL_APP_IDS.ORG_SETTINGS,
    flagshipModel: FlagshipModelOptions.ORG,
    nameGetter: (record) => record.name,
    tagTypes: ['Org', 'Orgs'],
  },
  [ALL_RECORD_TYPE_IDS.RFQ]: {
    id: ALL_RECORD_TYPE_IDS.RFQ,
    singularName: 'RFQ',
    pluralName: 'RFQs',
    app: ALL_APP_IDS.SOURCING,
    view: ALL_VIEW_IDS.RFQ,
    tagRecord: true,
    customPropertyModel: CustomPropertyModel.RFQ,
    useGetQuery: useGetRfqsQuery,
    approveMutation: useApproveRfqsMutation,
    unapproveMutation: useUnapproveRfqsMutation,
    archiveMutation: useArchiveRfqsMutation,
    unarchiveMutation: useUnarchiveRfqsMutation,
    voidMutation: useVoidRfqsMutation,
    unvoidMutation: useUnvoidRfqsMutation,
    deleteMutation: useDeleteRfqsMutation,
    updateMutation: useUpdateRfqsMutation,
    externallySyncableRecord: true,
    flagshipModel: FlagshipModelOptions.RFQ,
    generatePdfMutation: useGenerateRfqPdfMutation,
    generatePdfDocumentType: StockDocType.RFQ,
    nameGetter: (record) => `RFQ #${record.rfqCode}`,
    childRecordTypes: [
      ALL_RECORD_TYPE_IDS.RFQ_LINE,
      ALL_RECORD_TYPE_IDS.RFQ_SUPPLIER_JOIN,
    ],
    statusGetter: getRfqStatusSteps,
    statusSchema: rfqStatusSchema,
    reportingTemplateName: 'rfq',
  },
  [ALL_RECORD_TYPE_IDS.RFQ_LINE]: {
    id: ALL_RECORD_TYPE_IDS.RFQ_LINE,
    singularName: 'RFQ Line',
    pluralName: 'RFQ Lines',
    app: ALL_APP_IDS.SOURCING,
    tagRecord: true,
    customPropertyModel: CustomPropertyModel.RFQ_LINE,
    deleteMutation: useDeleteRfqLinesMutation,
    updateMutation: useUpdateRfqLinesMutation,
    teamRecord: true,
    flagshipModel: FlagshipModelOptions.RFQ_LINE,
    nameGetter: (record) =>
      `${_.toNumber(record.quant).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getOrgPartOrDescription(record)}`,
  },
  [ALL_RECORD_TYPE_IDS.RFQ_SUPPLIER_JOIN]: {
    id: ALL_RECORD_TYPE_IDS.RFQ_SUPPLIER_JOIN,
    singularName: 'Recipient',
    pluralName: 'Recipients',
    app: ALL_APP_IDS.SOURCING,
    flagshipModel: FlagshipModelOptions.RFQ_SUPPLIER_JOIN,
  },
  [ALL_RECORD_TYPE_IDS.QUOTE]: {
    id: ALL_RECORD_TYPE_IDS.QUOTE,
    singularName: 'Quote',
    pluralName: 'Quotes',
    app: ALL_APP_IDS.SOURCING,
    view: ALL_VIEW_IDS.QUOTE,
    tagRecord: true,
    customPropertyModel: CustomPropertyModel.SUPPLIER_QUOTE,
    useGetQuery: useGetSupplierQuotesQuery,
    deleteMutation: useDeleteSupplierQuoteMutation,
    updateMutation: useUpdateSupplierQuoteMutation,
    archiveMutation: useArchiveSupplierQuoteActionMutation,
    unarchiveMutation: useUnarchiveSupplierQuoteActionMutation,
    externallySyncableRecord: true,
    flagshipModel: FlagshipModelOptions.SUPPLIER_QUOTE,
    nameGetter: (record) =>
      `${record.orgSupplier.name} Quote${
        record.quoteNumber ? ` #${record.quoteNumber}` : ''
      }`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.QUOTE_LINE],
    createWithAiDocTypes: [StockDocType.QUOTE],
    createWithAiInvalidateTags: ['SupplierQuotes'],
    statusGetter: getSupplierQuoteStatusSteps,
    statusSchema: supplierQuoteStatusSchema,
    reportingTemplateName: 'supplierquote',
  },
  [ALL_RECORD_TYPE_IDS.QUOTE_LINE]: {
    id: ALL_RECORD_TYPE_IDS.QUOTE_LINE,
    singularName: 'Quote Line',
    pluralName: 'Quote Lines',
    app: ALL_APP_IDS.SOURCING,
    tagRecord: true,
    customPropertyModel: CustomPropertyModel.SUPPLIER_QUOTE_LINE,
    deleteMutation: useDeleteSupplierQuoteLineMutation,
    updateMutation:
      useUpdateSupplierQuoteLineMutation as PatchMutation<SupplierQuoteLineSchema>,
    approveMutation: useApproveSupplierQuoteLineActionMutation,
    unapproveMutation: useUnapproveSupplierQuoteLineActionMutation,
    voidMutation: useVoidSupplierQuoteLineActionMutation,
    unvoidMutation: useUnvoidSupplierQuoteLineActionMutation,
    teamRecord: true,
    flagshipModel: FlagshipModelOptions.SUPPLIER_QUOTE_LINE,
    nameGetter: (record) =>
      `${_.toNumber(record.maximum).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getOrgPartOrDescription(record)}`,
  },
  [ALL_RECORD_TYPE_IDS.SUPPLIER_BILL]: {
    id: ALL_RECORD_TYPE_IDS.SUPPLIER_BILL,
    singularName: 'Bill',
    pluralName: 'Bills',
    app: ALL_APP_IDS.PROCUREMENT,
    view: ALL_VIEW_IDS.SUPPLIER_BILLS,
    customPropertyModel: CustomPropertyModel.SUPPLIER_BILL,
    useGetQuery: useGetSupplierBillsQuery,
    approveMutation: useApproveSupplierBillActionMutation,
    unapproveMutation: useUnapproveSupplierBillActionMutation,
    archiveMutation: useArchiveSupplierBillActionMutation,
    unarchiveMutation: useUnarchiveSupplierBillActionMutation,
    voidMutation: useVoidSupplierBillActionMutation,
    unvoidMutation: useUnvoidSupplierBillActionMutation,
    deleteMutation: useDeleteSupplierBillMutation,
    updateMutation: useUpdateSupplierBillMutation,
    externallySyncableRecord: true,
    flagshipModel: FlagshipModelOptions.SUPPLIER_BILL,
    nameGetter: (record) =>
      `${record.orgSupplier.name} Bill${
        record.invoiceNumber ? ` #${record.invoiceNumber}` : ''
      }`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.SUPPLIER_BILL_LINE],
    requireAdminApprovals: true,
    createWithAiDocTypes: [StockDocType.INVOICE],
    createWithAiInvalidateTags: ['SupplierBills'],
    statusGetter: getSupplierBillStatusSteps,
    statusSchema: supplierBillStatusSchema,
    reportingTemplateName: 'supplierbill',
  },
  [ALL_RECORD_TYPE_IDS.SUPPLIER_BILL_LINE]: {
    id: ALL_RECORD_TYPE_IDS.SUPPLIER_BILL_LINE,
    singularName: 'Bill Line',
    pluralName: 'Bill Lines',
    app: ALL_APP_IDS.PROCUREMENT,
    customPropertyModel: CustomPropertyModel.SUPPLIER_BILL_LINE,
    deleteMutation: useDeleteSupplierBillLineMutation,
    updateMutation: useUpdateSupplierBillLineMutation,
    flagshipModel: FlagshipModelOptions.SUPPLIER_BILL_LINE,
    nameGetter: (record) =>
      `${_.toNumber(record.quant).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getV2OrgPartOrDescription(record)} = ${formatPrice(
        record.lineTotal,
        2
      )}`,
  },
  [ALL_RECORD_TYPE_IDS.STOCK_LOT]: {
    id: ALL_RECORD_TYPE_IDS.STOCK_LOT,
    singularName: 'Stock Lot',
    pluralName: 'Stock Lots',
    app: ALL_APP_IDS.STOCK,
    view: ALL_VIEW_IDS.STOCK_LOTS,
    flagshipModel: FlagshipModelOptions.STOCK_LOT,
    nameGetter: (record) => {
      const abbreviation =
        UnitOfMeasureDetails[record.dimensionUnit].abbreviation;
      return `${_.toNumber(record.dimensionValue).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}${abbreviation} ${getPart(record)} (${record.lotId})`;
    },
    statusGetter: getStockLotStatusSteps,
  },
  [ALL_RECORD_TYPE_IDS.SHIPMENT]: {
    id: ALL_RECORD_TYPE_IDS.SHIPMENT,
    singularName: 'Shipment',
    pluralName: 'Shipments',
    app: ALL_APP_IDS.KITTING,
    view: ALL_VIEW_IDS.SHIPMENTS,
    flagshipModel: FlagshipModelOptions.SHIPMENT,
    generatePdfMutation: useGenerateShipmentPdfMutation,
    generatePdfDocumentType: StockDocType.PACKING_SLIP,
    nameGetter: (record) =>
      `Shipment #${record.shipmentCode}: ${getAddressLabel(
        record.shipFrom
      )} → ${getAddressLabel(record.shipTo)}${
        record.shippedAt ? ` (${formatToShortDate(record.shippedAt)})` : ''
      }`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.SHIPMENT_LINE],
    statusGetter: getShipmentStatusSteps,
    statusSchema: shipmentStatusSchema,
    useGetQuery: useGetShipmentsClientV2Query,
    reportingTemplateName: 'shipment',
  },
  [ALL_RECORD_TYPE_IDS.SHIPMENT_LINE]: {
    id: ALL_RECORD_TYPE_IDS.SHIPMENT_LINE,
    singularName: 'Shipment Line',
    pluralName: 'Shipment Lines',
    app: ALL_APP_IDS.KITTING,
    flagshipModel: FlagshipModelOptions.SHIPMENT_LINE,
    nameGetter: (record) =>
      `${_.toNumber(record.shippedQuant).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${record.stockLot ? getPart(record.stockLot) : '-'}`,
  },
  [ALL_RECORD_TYPE_IDS.KIT_REQUEST]: {
    id: ALL_RECORD_TYPE_IDS.KIT_REQUEST,
    singularName: 'Kit',
    pluralName: 'Kits',
    app: ALL_APP_IDS.KITTING,
    view: ALL_VIEW_IDS.KITS,
    flagshipModel: FlagshipModelOptions.KIT_REQUEST,
    nameGetter: (record) =>
      `Kit #${record.kitCode}: ${getAddressLabel(record.shipTo)}${
        record.name ? ` - ${record.name}` : ''
      }${
        record.approvedAt ? ` (${formatToShortDate(record.approvedAt)})` : ''
      }`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.KIT_REQUEST_LINE],
    statusGetter: getKitRequestStatusSteps,
    statusSchema: kitRequestStatusSchema,
    useGetQuery: useGetKitRequestsQuery,
    reportingTemplateName: 'kitrequest',
  },
  [ALL_RECORD_TYPE_IDS.KIT_REQUEST_LINE]: {
    id: ALL_RECORD_TYPE_IDS.KIT_REQUEST_LINE,
    singularName: 'Kit Line',
    pluralName: 'Kit Lines',
    app: ALL_APP_IDS.KITTING,
    flagshipModel: FlagshipModelOptions.KIT_REQUEST_LINE,
    customPropertyModel: CustomPropertyModel.KIT_REQUEST_LINE,
    deleteMutation: useDeleteKitRequestLinesMutation,
    updateMutation: useUpdateKitRequestLinesMutation,
    nameGetter: (record) =>
      `${_.toNumber(record.targetQuantity).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getV2OrgPartFromV2OrgParts(record)}`,
  },
  [ALL_RECORD_TYPE_IDS.ORG_SUPPLIER]: {
    id: ALL_RECORD_TYPE_IDS.ORG_SUPPLIER,
    singularName: 'Supplier',
    pluralName: 'Suppliers',
    app: ALL_APP_IDS.SUPPLIERS,
    view: ALL_VIEW_IDS.SUPPLIERS,
    flagshipModel: FlagshipModelOptions.ORG_SUPPLIER,
    tagRecord: true,
    nameGetter: (record) => `${record.name}`,
  },
  [ALL_RECORD_TYPE_IDS.V2_ORG_PART]: {
    id: ALL_RECORD_TYPE_IDS.V2_ORG_PART,
    singularName: 'Part',
    pluralName: 'Parts',
    app: ALL_APP_IDS.PARTS,
    flagshipModel: FlagshipModelOptions.V2_ORG_PART,
    customPropertyModel: CustomPropertyModel.V2_ORG_PART,
    updateMutation: useUpdateOrgPartMutation,
    nameGetter: (record) => getPart({ part: record }) || '',
  },
  [ALL_RECORD_TYPE_IDS.PURCHASE]: {
    id: ALL_RECORD_TYPE_IDS.PURCHASE,
    singularName: 'Purchase Order',
    pluralName: 'Purchase Orders',
    app: ALL_APP_IDS.PROCUREMENT,
    view: ALL_VIEW_IDS.PURCHASE_ORDERS,
    flagshipModel: FlagshipModelOptions.PURCHASE,
    generatePdfMutation: useGeneratePurchaseOrderPdfMutation,
    generatePdfDocumentType: StockDocType.PURCHASE_ORDER,
    nameGetter: (record) =>
      `${record.orgSupplier.name} Purchase Order #${record.poNumber}${
        record.approvedAt ? ` (${formatToShortDate(record.approvedAt)})` : ''
      }`,
    childRecordTypes: [
      ALL_RECORD_TYPE_IDS.PURCHASE_LINE,
      ALL_RECORD_TYPE_IDS.NO_PART_PURCHASE_LINE,
      ALL_RECORD_TYPE_IDS.PURCHASE_EVENT,
    ],
    requireAdminApprovals: true,
    createWithAiDocTypes: [
      StockDocType.PURCHASE_ORDER,
      StockDocType.INVOICE,
      StockDocType.PACKING_SLIP,
      StockDocType.SHIP_NOTIFICATION,
      StockDocType.PURCHASE_CONFIRMATION,
      StockDocType.PURCHASE_UPDATE,
      StockDocType.QUOTE,
    ],
    createWithAiInvalidateTags: ['PurchaseOrders'],
    statusGetter: getPurchaseOrderStatusSteps,
    statusSchema: purchaseOrderStatusSchema,
    useGetQuery: useGetPurchaseOrdersClientV2Query,
    reportingTemplateName: 'purchaseorder',
  },
  [ALL_RECORD_TYPE_IDS.PURCHASE_LINE]: {
    id: ALL_RECORD_TYPE_IDS.PURCHASE_LINE,
    singularName: 'Purchase Order Line',
    pluralName: 'Purchase Order Lines',
    app: ALL_APP_IDS.PROCUREMENT,
    flagshipModel: FlagshipModelOptions.PURCHASE_LINE,
    nameGetter: (record) =>
      `${_.toNumber(record.quant).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getOrgPartOrDescription(record)} = ${formatPrice(
        record.lineTotalPaid,
        2
      )}`,
  },
  [ALL_RECORD_TYPE_IDS.NO_PART_PURCHASE_LINE]: {
    id: ALL_RECORD_TYPE_IDS.NO_PART_PURCHASE_LINE,
    singularName: 'Other Purchase Order Line',
    pluralName: 'Other Purchase Order Lines',
    app: ALL_APP_IDS.PROCUREMENT,
    flagshipModel: FlagshipModelOptions.NO_PART_PURCHASE_LINE,
  },
  [ALL_RECORD_TYPE_IDS.PURCHASE_REQUEST]: {
    id: ALL_RECORD_TYPE_IDS.PURCHASE_REQUEST,
    singularName: 'Requisition Line',
    pluralName: 'Requisition Lines',
    app: ALL_APP_IDS.REQUISITIONS,
    flagshipModel: FlagshipModelOptions.PURCHASE_REQUEST,
    tagRecord: true,
    nameGetter: (record) =>
      `Requisition for ${_.toNumber(record.quant).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getPartOrDescription(record)}`,
    requireAdminApprovals: true,
    updateMutation: useUpdatePurchaseRequestsV2Mutation,
  },
  [ALL_RECORD_TYPE_IDS.PURCHASE_REQUEST_GROUP]: {
    id: ALL_RECORD_TYPE_IDS.PURCHASE_REQUEST_GROUP,
    singularName: 'Requisition',
    pluralName: 'Requisitions',
    app: ALL_APP_IDS.REQUISITIONS,
    view: ALL_VIEW_IDS.REQUISITIONS_TABLE,
    flagshipModel: FlagshipModelOptions.PURCHASE_REQUEST_GROUP,
    tagRecord: true,
    nameGetter: (record) => `Requisition ${record.name}`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.PURCHASE_REQUEST],
    requireAdminApprovals: true,
    createWithAiDocTypes: [
      StockDocType.QUOTE,
      StockDocType.RFQ,
      StockDocType.PURCHASE_REQUEST,
    ],
    createWithAiInvalidateTags: ['PurchaseRequestGroups'],
  },
  [ALL_RECORD_TYPE_IDS.PURCHASE_EVENT]: {
    id: ALL_RECORD_TYPE_IDS.PURCHASE_EVENT,
    singularName: 'Purchase Update',
    pluralName: 'Purchase Updates',
    app: ALL_APP_IDS.PROCUREMENT,
    flagshipModel: FlagshipModelOptions.PURCHASE_EVENT,
  },
  [ALL_RECORD_TYPE_IDS.PRODUCTION_RUN]: {
    id: ALL_RECORD_TYPE_IDS.PRODUCTION_RUN,
    singularName: 'Program',
    pluralName: 'Programs',
    app: ALL_APP_IDS.PRODUCTION,
    flagshipModel: FlagshipModelOptions.PRODUCTION_RUN,
    nameGetter: (record) =>
      `Program ${record.name}${
        record.referenceNumber ? ` (${record.referenceNumber})` : ''
      }`,
    statusGetter: getProductionRunStatusSteps,
    statusSchema: productionRunStatusSchema,
    useGetQuery: useGetProgramsQuery,
  },
  [ALL_RECORD_TYPE_IDS.PRODUCTION_RUN_PART]: {
    id: ALL_RECORD_TYPE_IDS.PRODUCTION_RUN_PART,
    singularName: 'Program Part',
    pluralName: 'Program Parts',
    app: ALL_APP_IDS.PRODUCTION,
    // @ts-expect-error: Program Parts don't correlate to a Flagship model directly
    flagshipModel: undefined,
    nameGetter: (record) => `Program Part: ${getPart(record)}`,
  },
  [ALL_RECORD_TYPE_IDS.BOM]: {
    id: ALL_RECORD_TYPE_IDS.BOM,
    singularName: 'BOM',
    pluralName: 'BOMs',
    app: ALL_APP_IDS.BOMS,
    flagshipModel: FlagshipModelOptions.BOM,
    nameGetter: (record) => `${record.name} Version: ${record.version}`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.BOM_LINE],
    statusGetter: getBomStatusSteps,
    statusSchema: bomStatusSchema,
    useGetQuery: useGetClientV2BomsQuery,
  },
  [ALL_RECORD_TYPE_IDS.BOM_LINE]: {
    id: ALL_RECORD_TYPE_IDS.BOM_LINE,
    singularName: 'BOM Line',
    pluralName: 'BOM Lines',
    app: ALL_APP_IDS.BOMS,
    flagshipModel: FlagshipModelOptions.BOM_LINE,
    nameGetter: (record) => `${getPart(record)}`,
  },
  [ALL_RECORD_TYPE_IDS.PART]: {
    id: ALL_RECORD_TYPE_IDS.PART,
    singularName: 'Part',
    pluralName: 'Parts',
    app: ALL_APP_IDS.PART_SEARCH,
    flagshipModel: FlagshipModelOptions.ORG_PART,
  },
  [ALL_RECORD_TYPE_IDS.BARCODE_LABEL]: {
    id: ALL_RECORD_TYPE_IDS.BARCODE_LABEL,
    singularName: 'Barcode Label',
    pluralName: 'Barcode Labels',
    app: ALL_APP_IDS.STOCK,
    flagshipModel: FlagshipModelOptions.BARCODE_LABEL,
  },
  [ALL_RECORD_TYPE_IDS.UNIFIED_BOM]: {
    id: ALL_RECORD_TYPE_IDS.UNIFIED_BOM,
    singularName: 'BOM',
    pluralName: 'BOMs',
    app: ALL_APP_IDS.BOMS,
    // @ts-expect-error: Need to figure out how to handle that BOM and UNIFIED_BOM are the same model. BOM will go away soon
    flagshipModel: undefined,
    nameGetter: (record) => `${record.name} Version: ${record.version}`,
    statusGetter: getUnifiedBomStatusSteps,
    useGetQuery: useGetUnifiedBomsQuery,
    approveMutation: useApproveUnifiedBomsMutation,
    unapproveMutation: useUnapproveUnifiedBomsMutation,
    archiveMutation: useArchiveUnifiedBomsMutation,
    unarchiveMutation: useUnarchiveUnifiedBomsMutation,
    deleteMutation: useDeleteUnifiedBomsMutation,
    updateMutation: useUpdateUnifiedBomsMutation,
    lockMutation: useLockUnifiedBomsMutation,
    unlockMutation: useUnlockUnifiedBomsMutation,
    hasCreatedBy: true,
  },
  [ALL_RECORD_TYPE_IDS.CART_PURCHASE_REQUEST_GROUP]: {
    id: ALL_RECORD_TYPE_IDS.CART_PURCHASE_REQUEST_GROUP,
    singularName: 'Cart',
    pluralName: 'Carts',
    app: ALL_APP_IDS.SIMPLIFIED_BUYING_ORDERS,
    view: ALL_VIEW_IDS.ALL_ORDERS,
    flagshipModel: FlagshipModelOptions.PURCHASE_REQUEST_GROUP,
    tagRecord: true,
    nameGetter: (record) => `Cart: ${record.name}`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.CART_PURCHASE_REQUEST],
    updateMutation: useUpdatePurchaseRequestGroupsMutation,
    customPropertyModel: CustomPropertyModel.PURCHASE_REQUEST_GROUP,
    reportingTemplateName: 'cartpurchaserequestgroup',
  },
  [ALL_RECORD_TYPE_IDS.CART_PURCHASE_REQUEST]: {
    id: ALL_RECORD_TYPE_IDS.CART_PURCHASE_REQUEST,
    singularName: 'Cart Line',
    pluralName: 'Cart Lines',
    app: ALL_APP_IDS.SIMPLIFIED_BUYING_ORDERS,
    flagshipModel: FlagshipModelOptions.PURCHASE_REQUEST,
    tagRecord: true,
    nameGetter: (record) =>
      `${_.toNumber(record.quant).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getPartOrDescription(record)}`,
    requireAdminApprovals: true,
    updateMutation: useUpdatePurchaseRequestsV2Mutation,
    customPropertyModel: CustomPropertyModel.PURCHASE_REQUEST,
  },
  [ALL_RECORD_TYPE_IDS.RFQ_PURCHASE_REQUEST_GROUP]: {
    id: ALL_RECORD_TYPE_IDS.RFQ_PURCHASE_REQUEST_GROUP,
    singularName: 'RFQ',
    pluralName: 'RFQs',
    app: ALL_APP_IDS.SOURCING,
    view: ALL_VIEW_IDS.SOURCING_RFQS,
    flagshipModel: FlagshipModelOptions.PURCHASE_REQUEST_GROUP,
    tagRecord: true,
    nameGetter: (record) => `RFQ: ${record.name}`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.RFQ_PURCHASE_REQUEST],
    requireAdminApprovals: true,
    updateMutation: useUpdatePurchaseRequestGroupsMutation,
    customPropertyModel: CustomPropertyModel.PURCHASE_REQUEST_GROUP,
    approveMutation: useApprovePurchaseRequestGroupsActionMutation,
    reportingTemplateName: 'rfqpurchaserequestgroup',
  },
  [ALL_RECORD_TYPE_IDS.RFQ_PURCHASE_REQUEST]: {
    id: ALL_RECORD_TYPE_IDS.RFQ_PURCHASE_REQUEST,
    singularName: 'RFQ Line',
    pluralName: 'RFQ Lines',
    app: ALL_APP_IDS.SOURCING,
    flagshipModel: FlagshipModelOptions.PURCHASE_REQUEST,
    tagRecord: true,
    nameGetter: (record) =>
      `${_.toNumber(record.quant).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getPartOrDescription(record)}`,
    requireAdminApprovals: true,
    updateMutation: useUpdatePurchaseRequestsV2Mutation,
    customPropertyModel: CustomPropertyModel.PURCHASE_REQUEST,
  },
  [ALL_RECORD_TYPE_IDS.SUPPLIER_PURCHASE_ORDER]: {
    id: ALL_RECORD_TYPE_IDS.SUPPLIER_PURCHASE_ORDER,
    singularName: 'Supplier Order',
    pluralName: 'Supplier Orders',
    app: ALL_APP_IDS.SIMPLIFIED_BUYING_ORDERS,
    view: ALL_VIEW_IDS.SUPPLIER_ORDERS,
    flagshipModel: FlagshipModelOptions.PURCHASE,
    nameGetter: (record) =>
      `${record.orgSupplier.name} Order #${record.poNumber}${
        record.approvedAt ? ` (${formatToShortDate(record.approvedAt)})` : ''
      }`,
    childRecordTypes: [ALL_RECORD_TYPE_IDS.SUPPLIER_PURCHASE_ORDER_LINE],
    statusGetter: getSupplierPurchaseOrderStatusSteps,
    statusSchema: supplierPurchaseOrderStatusSchema,
    reportingTemplateName: 'supplierpurchaseorder',
  },
  [ALL_RECORD_TYPE_IDS.SUPPLIER_PURCHASE_ORDER_LINE]: {
    id: ALL_RECORD_TYPE_IDS.SUPPLIER_PURCHASE_ORDER_LINE,
    singularName: 'Supplier Order Line',
    pluralName: 'Supplier Order Lines',
    app: ALL_APP_IDS.SIMPLIFIED_BUYING_ORDERS,
    flagshipModel: FlagshipModelOptions.PURCHASE_LINE,
    nameGetter: (record) =>
      `${_.toNumber(record.quant).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}x ${getOrgPartOrDescription(record)} = ${formatPrice(
        record.lineTotalPaid,
        2
      )}`,
  },
};

export const ALL_RECORD_TYPES_BY_MODEL = _.chain(ALL_RECORD_TYPES)
  .values()
  .keyBy('flagshipModel')
  .value();

export const relatedRecordTypeToRecordType: {
  [key in RelatedRecordType]: keyof typeof ALL_RECORD_TYPE_IDS | undefined;
} = {
  [RelatedRecordType.BOM]: ALL_RECORD_TYPE_IDS.BOM,
  [RelatedRecordType.PURCHASE_ORDER]: ALL_RECORD_TYPE_IDS.PURCHASE,
  [RelatedRecordType.PRODUCTION_RUN]: ALL_RECORD_TYPE_IDS.PRODUCTION_RUN,
  [RelatedRecordType.STOCK_LOT]: ALL_RECORD_TYPE_IDS.STOCK_LOT,
  [RelatedRecordType.STOCK_LOCATION]: undefined,
  [RelatedRecordType.SHIPMENT]: ALL_RECORD_TYPE_IDS.SHIPMENT,
  [RelatedRecordType.KIT_REQUEST]: ALL_RECORD_TYPE_IDS.KIT_REQUEST,
  [RelatedRecordType.SUPPLIER_BILL]: ALL_RECORD_TYPE_IDS.SUPPLIER_BILL,
  [RelatedRecordType.PURCHASE_REQUEST_GROUP]:
    ALL_RECORD_TYPE_IDS.PURCHASE_REQUEST_GROUP,
  [RelatedRecordType.RFQ]: ALL_RECORD_TYPE_IDS.RFQ,
  [RelatedRecordType.SUPPLIER_QUOTE]: ALL_RECORD_TYPE_IDS.QUOTE,
  [RelatedRecordType.PART]: ALL_RECORD_TYPE_IDS.V2_ORG_PART,
  [RelatedRecordType.SNAPSHOT]: undefined,
  [RelatedRecordType.ORDER]: undefined,
  [RelatedRecordType.USER]: undefined,
  [RelatedRecordType.TEAM]: undefined,
  [RelatedRecordType.CART_PURCHASE_REQUEST_GROUP]:
    ALL_RECORD_TYPE_IDS.CART_PURCHASE_REQUEST_GROUP,
  [RelatedRecordType.RFQ_PURCHASE_REQUEST_GROUP]:
    ALL_RECORD_TYPE_IDS.RFQ_PURCHASE_REQUEST_GROUP,
  [RelatedRecordType.SUPPLIER_PURCHASE_ORDER]:
    ALL_RECORD_TYPE_IDS.SUPPLIER_PURCHASE_ORDER,
};
